// @ts-strict-ignore
import type { Dayjs } from 'dayjs';
import React from 'react';
import type { Option } from '@usealloy/component-library';
import { MultiSelect } from '@usealloy/component-library';

import { allHandler } from '../../helpers';
import {
  FiltersWrapper,
  GeneralFilterWrapper,
  LastUpdatedFilterWrapper,
  StyledDateRangePicker,
  SuppressedFilterWrapper,
} from '../../styles';
import type { FilterOptions, FilterState, SetFilterState } from '../../types';

interface WatchlistFiltersProps {
  filterOptions: FilterOptions;
  filterState: FilterState;
  needsReview?: boolean;
  setFilters: SetFilterState;
  availableFilters: { [key: string]: boolean };
}

const WatchlistFilters = ({
  filterOptions: {
    fieldOptions,
    typeOptions,
    statusOptions,
    strengthOptions,
    suppressedOptions,
    screeningTypeOptions,
    serviceOptions,
  },
  filterState,
  availableFilters,
  needsReview = false,
  setFilters,
}: WatchlistFiltersProps) => {
  const {
    selectedFields,
    selectedLastUpdated,
    selectedStatuses,
    selectedStrengths,
    selectedSuppressed,
    selectedTypes,
    selectedScreeningTypes,
    selectedServices,
  } = filterState;

  const {
    isSuppressed: isSuppressedAvailable,
    lastUpdated: isLastUpdatedAvailable,
    matchFields: isMatchFieldsAvailable,
    matchStrength: isMatchStrengthAvailable,
    matchTypes: isMatchTypesAvailable,
    screeningType: isScreeningTypeAvailable,
    service: isServiceAvailable,
    status: isStatusAvailable,
  } = availableFilters;

  const hasNonSourceScreeningTypes = screeningTypeOptions.some(({ label }) => !['All', 'Source'].includes(label));

  return (
    <FiltersWrapper
      data-testId="watchlist-filters"
      className="watchlist-list-tab-filters"
      direction="row"
      gap={3}
      justifyContent="flex-start"
    >
      { isMatchTypesAvailable && (
      <GeneralFilterWrapper>
        <MultiSelect
          onChange={(selected: Option[]) => {
            setFilters(state => ({
              ...state,
              selectedTypes: allHandler(selectedTypes, selected),
            }));
          }}
          label="Type"
          options={typeOptions}
          selectedOptions={selectedTypes}
          visibleSelectedOptions
          data-dd-action-name="alloydd-watchlistMatches-filterBy-Type"
        />
      </GeneralFilterWrapper>
      )}
      { isStatusAvailable && !needsReview && (
        <GeneralFilterWrapper>
          <MultiSelect
            onChange={(selected: Option[]) => {
              setFilters(state => ({
                ...state,
                selectedStatuses: allHandler(selectedStatuses, selected),
              }));
            }}
            label="Status"
            options={statusOptions}
            selectedOptions={selectedStatuses}
            visibleSelectedOptions
            data-dd-action-name="alloydd-watchlistMatches-filterBy-Status"
          />
        </GeneralFilterWrapper>
      )}
      { isMatchFieldsAvailable && (
      <GeneralFilterWrapper>
        <MultiSelect
          onChange={(selected: Option[]) => {
            setFilters(state => ({
              ...state,
              selectedFields: allHandler(selectedFields, selected),
            }));
          }}
          label="Matched Fields"
          options={fieldOptions}
          selectedOptions={selectedFields}
          visibleSelectedOptions
          data-dd-action-name="alloydd-watchlistMatches-filterBy-MatchedFields"
        />
      </GeneralFilterWrapper>
      )}
      { isMatchStrengthAvailable && (
      <GeneralFilterWrapper>
        <MultiSelect
          onChange={(selected: Option[]) => {
            setFilters(state => ({
              ...state,
              selectedStrengths: allHandler(selectedStrengths, selected),
            }));
          }}
          label="Strength"
          options={strengthOptions}
          selectedOptions={selectedStrengths}
          visibleSelectedOptions
          data-dd-action-name="alloydd-watchlistMatches-filterBy-Strength"
        />
      </GeneralFilterWrapper>
      )}
      { isScreeningTypeAvailable && hasNonSourceScreeningTypes && (
        <GeneralFilterWrapper>
          <MultiSelect
            onChange={(selected: Option[]) => {
              setFilters(state => ({
                ...state,
                selectedScreeningTypes: allHandler(selectedScreeningTypes, selected),
              }));
            }}
            label="Screening Type"
            options={screeningTypeOptions}
            selectedOptions={selectedScreeningTypes}
            visibleSelectedOptions
            data-dd-action-name="alloydd-watchlistMatches-filterBy-ScreeningType"
          />
        </GeneralFilterWrapper>
      )}
      { isServiceAvailable && (
      <GeneralFilterWrapper>
        <MultiSelect
          onChange={(selected: Option[]) => {
            setFilters(state => ({
              ...state,
              selectedServices: allHandler(selectedServices, selected),
            }));
          }}
          label="Service"
          options={serviceOptions}
          selectedOptions={selectedServices}
          visibleSelectedOptions
          data-dd-action-name="alloydd-watchlistMatches-filterBy-Service"
        />
      </GeneralFilterWrapper>
      )}
      { isLastUpdatedAvailable && (
      <LastUpdatedFilterWrapper>
        <StyledDateRangePicker
          allowClear
          label="Last Updated"
          onChange={dateRange => {
            setFilters(state => ({
              ...state,
              selectedLastUpdated: dateRange as [Dayjs, Dayjs],
            }));
          }}
          value={selectedLastUpdated}
          data-dd-action-name="alloydd-watchlistMatches-filterBy-LastUpdated"
        />
      </LastUpdatedFilterWrapper>
      )}
      { isSuppressedAvailable && !needsReview && (
        <SuppressedFilterWrapper>
          <MultiSelect
            onChange={(selected: Option) => {
              setFilters(state => ({
                ...state,
                selectedSuppressed: selected,
              }));
            }}
            label="Suppressed"
            options={suppressedOptions}
            selectedOptions={selectedSuppressed}
            isMulti={false}
            visibleSelectedOptions
            data-dd-action-name="alloydd-watchlistMatches-filterBy-Suppressed"
          />
        </SuppressedFilterWrapper>
      )}
    </FiltersWrapper>
  );
};

export default WatchlistFilters;
